import { ExerciseType } from '@org/model';
import { ContentType } from '../../../types/contents';
import type {
  StudentContent,
  StudentExerciseContent,
} from './use-class-blocks';

/**
 * Derivation helpers for the zine "issue" class page: each block's agenda
 * kind, estimated minutes, and done state are DERIVED from its contents.
 *
 * Pure functions — no React, no tRPC. Kinds are machine keys; i18n labels
 * are resolved by the caller.
 */

export type ZineBlockKind = 'read' | 'listen' | 'practice' | 'speak';

const READING_WORDS_PER_MINUTE = 180;
const VIDEO_SECONDS = 180; // ~3 min per video
const AUDIO_FALLBACK_SECONDS = 120; // when audio length is unknown
const AUTO_GRADED_EXERCISE_SECONDS = 90; // ~1.5 min per auto-checked exercise
const WRITTEN_EXERCISE_SECONDS = 240; // ~4 min per written answer
const CONVERSATIONAL_FALLBACK_SECONDS = 300; // default conversational duration
const RECORDED_FALLBACK_SECONDS = 60; // default recording duration

const SPEAK_EXERCISE_TYPES: ReadonlySet<ExerciseType> = new Set([
  ExerciseType.Conversational,
  ExerciseType.Recorded,
]);

const WRITTEN_EXERCISE_TYPES: ReadonlySet<ExerciseType> = new Set([
  ExerciseType.Written,
  ExerciseType.LongAnswer,
  ExerciseType.ShortAnswer,
  ExerciseType.FileUpload,
]);

function isExerciseContent(
  content: StudentContent,
): content is StudentExerciseContent {
  return content.type === ContentType.Exercise;
}

/**
 * Agenda kind for a block, from its contents:
 * - any Conversational or Recorded exercise → 'speak'
 * - else any other exercise → 'practice'
 * - else any audio/video content → 'listen'
 * - else → 'read'
 */
export function deriveBlockKind(contents: StudentContent[]): ZineBlockKind {
  const exercises = contents.filter(isExerciseContent);

  if (exercises.some((ex) => SPEAK_EXERCISE_TYPES.has(ex.exerciseSubtype))) {
    return 'speak';
  }
  if (exercises.length > 0) {
    return 'practice';
  }
  if (
    contents.some(
      (content) =>
        content.type === ContentType.Audio ||
        content.type === ContentType.Video,
    )
  ) {
    return 'listen';
  }
  return 'read';
}

/**
 * Estimated minutes for a block (integer, minimum 1):
 * audio length (secs) + ~3 min per video + text word-count at 180 wpm +
 * ~1.5 min per auto-graded exercise + ~4 min per written exercise +
 * conversational/recorded maxDurationSec. Rounded up to whole minutes.
 */
export function deriveBlockMinutes(contents: StudentContent[]): number {
  const totalSeconds = contents.reduce(
    (sum, content) => sum + estimateContentSeconds(content),
    0,
  );

  return Math.max(1, Math.ceil(totalSeconds / 60));
}

/**
 * A block is "done" when it has at least one exercise and every exercise id
 * has an entry in the latest-submissions map.
 */
export function deriveBlockDone(
  contents: StudentContent[],
  latestSubmissionsByExerciseId: Record<string, unknown>,
): boolean {
  const exercises = contents.filter(isExerciseContent);
  if (exercises.length === 0) {
    return false;
  }

  return exercises.every((exercise) =>
    Boolean(latestSubmissionsByExerciseId[exercise.exerciseId]),
  );
}

function estimateContentSeconds(content: StudentContent): number {
  switch (content.type) {
    case ContentType.Text:
      return (countWords(content.text) / READING_WORDS_PER_MINUTE) * 60;
    case ContentType.Audio:
      return content.length && content.length > 0
        ? content.length
        : AUDIO_FALLBACK_SECONDS;
    case ContentType.Video:
      return VIDEO_SECONDS;
    case ContentType.Exercise:
      return estimateExerciseSeconds(content);
    // Images and documents are not part of the estimate formula.
    default:
      return 0;
  }
}

function estimateExerciseSeconds(content: StudentExerciseContent): number {
  if (content.exerciseSubtype === ExerciseType.Conversational) {
    return content.maxDurationSec && content.maxDurationSec > 0
      ? content.maxDurationSec
      : CONVERSATIONAL_FALLBACK_SECONDS;
  }
  if (content.exerciseSubtype === ExerciseType.Recorded) {
    return content.maxDurationSec && content.maxDurationSec > 0
      ? content.maxDurationSec
      : RECORDED_FALLBACK_SECONDS;
  }
  if (WRITTEN_EXERCISE_TYPES.has(content.exerciseSubtype)) {
    return WRITTEN_EXERCISE_SECONDS;
  }
  return AUTO_GRADED_EXERCISE_SECONDS;
}

function countWords(text: string | null | undefined): number {
  if (!text) {
    return 0;
  }
  return text
    .split(/\s+/)
    .filter((word) => word.trim().length > 0).length;
}
